/*
 * Bakeology Studios — Add to Cart option-validation feedback
 * ----------------------------------------------------------
 * Problem this solves:
 *   The Globo Product Options app blocks "Add to Cart" when a required option
 *   (e.g. Cake Flavor) has not been chosen, but its only feedback is a small,
 *   low-contrast "This field is required" line rendered near the TOP of a long
 *   options form. The Add to Cart button sits ~840px below that — roughly a full
 *   mobile viewport — so the customer taps the button, sees literally nothing
 *   happen, and concludes the site is broken.
 *
 * What this does (purely additive — it never blocks or submits anything):
 *   1. Watches for a blocked Add to Cart attempt.
 *   2. Smoothly scrolls the customer to the first unanswered required option.
 *   3. Pulses a highlight ring around that option so it's obvious.
 *   4. Shows a clearly visible toast naming the option that's missing.
 *   5. Makes Globo's own inline error text bigger / higher contrast.
 *
 * Safe by design: if Globo isn't present, or no option is in an error state,
 * every handler no-ops. It never calls preventDefault() and never interferes
 * with a valid add-to-cart.
 */
(function () {
  'use strict';

  var TOAST_ID = 'bk-option-toast';
  var STYLE_ID = 'bk-option-validation-styles';
  var HIGHLIGHT_CLASS = 'bk-option-highlight';
  var ERROR_SELECTOR = '.gpo-form__group.has-error, .gpo-element.has-error';
  var ACCENT = '#B3261E';
  var toastTimer = null;

  /* ------------------------------------------------------------------ */
  /* Styles                                                              */
  /* ------------------------------------------------------------------ */

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      /* highlight pulse on the offending field */
      '.' + HIGHLIGHT_CLASS + '{border-radius:8px;animation:bk-option-pulse 1.3s ease-out 2;}',
      '@keyframes bk-option-pulse{',
      '0%{box-shadow:0 0 0 0 rgba(179,38,30,.5);}',
      '70%{box-shadow:0 0 0 12px rgba(179,38,30,0);}',
      '100%{box-shadow:0 0 0 0 rgba(179,38,30,0);}}',

      /* make Globo's own error state far more legible */
      '.gpo-form__group.has-error .gpo-label .label-content,',
      '.gpo-element.has-error .gpo-label .label-content{color:' + ACCENT + ';font-weight:600;}',
      '.gpo-form__group.has-error .dropdown-button,',
      '.gpo-form__group.has-error .gpo-input,',
      '.gpo-element.has-error .dropdown-button,',
      '.gpo-element.has-error .gpo-input{box-shadow:inset 0 0 0 1px ' + ACCENT + ';}',
      '.gpo-form__group.has-error .help-block.error,',
      '.gpo-element.has-error .help-block.error{',
      'color:' + ACCENT + ' !important;font-weight:600 !important;font-size:14px !important;}',

      /* the toast */
      '#' + TOAST_ID + '{position:fixed;left:50%;bottom:88px;z-index:100000;',
      'transform:translateX(-50%) translateY(18px);',
      'max-width:min(520px,calc(100vw - 32px));box-sizing:border-box;',
      'background:#E8E0D0;color:#0E3A27;border-left:5px solid ' + ACCENT + ';',
      'padding:14px 18px;border-radius:10px;box-shadow:0 8px 28px rgba(0,0,0,.28);',
      'font-family:inherit;font-size:15px;line-height:1.45;letter-spacing:.01em;',
      'display:flex;align-items:center;gap:14px;cursor:pointer;',
      'opacity:0;pointer-events:none;transition:opacity .22s ease,transform .22s ease;}',
      '#' + TOAST_ID + '.bk-visible{opacity:1;transform:translateX(-50%) translateY(0);pointer-events:auto;}',
      '#' + TOAST_ID + ' .bk-toast-text{flex:1 1 auto;}',
      '#' + TOAST_ID + ' .bk-toast-cta{flex:0 0 auto;white-space:nowrap;font-weight:600;text-decoration:underline;}',
      '@media (max-width:749px){#' + TOAST_ID + '{bottom:78px;font-size:14px;padding:12px 15px;gap:10px;}}',
      '@media (prefers-reduced-motion:reduce){',
      '.' + HIGHLIGHT_CLASS + '{animation:none;box-shadow:0 0 0 3px rgba(179,38,30,.55);}',
      '#' + TOAST_ID + '{transition:none;}}'
    ].join('');
    document.head.appendChild(style);
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function errorGroups() {
    var nodes = document.querySelectorAll(ERROR_SELECTOR);
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      // only consider fields actually visible to the customer
      if (nodes[i].offsetParent !== null || nodes[i].getClientRects().length) out.push(nodes[i]);
    }
    return out;
  }

  function labelFor(group) {
    var el = group.querySelector('.gpo-label .label-content') || group.querySelector('.gpo-label');
    var text = el ? el.textContent || '' : '';
    text = text.replace(/\s+/g, ' ').replace(/\*/g, '').trim();
    return text || 'a required option';
  }

  function hideToast() {
    var toast = document.getElementById(TOAST_ID);
    if (toast) toast.classList.remove('bk-visible');
  }

  function buildToast() {
    var toast = document.getElementById(TOAST_ID);
    if (toast) return toast;
    toast = document.createElement('div');
    toast.id = TOAST_ID;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.innerHTML = '<span class="bk-toast-text"></span><span class="bk-toast-cta"></span>';
    toast.addEventListener('click', function () {
      var next = errorGroups()[0];
      if (next) revealGroup(next);
      hideToast();
    });
    document.body.appendChild(toast);
    return toast;
  }

  function showToast() {
    injectStyles();
    var groups = errorGroups();
    if (!groups.length) return;
    var toast = buildToast();
    var message = groups.length > 1
      ? 'Please complete ' + groups.length + ' required options before adding to cart.'
      : 'Please choose your ' + labelFor(groups[0]) + ' before adding to cart.';
    toast.querySelector('.bk-toast-text').textContent = message;
    toast.querySelector('.bk-toast-cta').textContent = 'Take me there';
    toast.classList.add('bk-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 7000);
  }

  /* Scrolling notes (verified in-browser on this theme, 28 Jul 2026):
       - Native smooth scrolling is a silent no-op on the product page. Both
         scrollIntoView({behavior:'smooth'}) and window.scrollTo({behavior:'smooth'})
         leave scrollY completely unchanged.
       - A hand-rolled requestAnimationFrame animation gets fought and undone,
         because the page lazy-loads a related-products row while it runs.
       - Plain, instant scrollIntoView({block:'center'}) works reliably.
     So: jump instantly, then re-assert once in case late-loading content
     shifted the page underneath us. Do not "improve" this to smooth scrolling
     without re-testing on a real product page. */
  function revealGroup(group) {
    injectStyles();

    function place() {
      try {
        group.scrollIntoView({ block: 'center', inline: 'nearest' });
      } catch (e) {
        group.scrollIntoView();
      }
    }
    place();
    setTimeout(place, 250);

    group.classList.remove(HIGHLIGHT_CLASS);
    void group.offsetWidth; // restart the animation
    group.classList.add(HIGHLIGHT_CLASS);
    setTimeout(function () { group.classList.remove(HIGHLIGHT_CLASS); }, 3200);
  }

  /* Returns true if a blocked attempt was detected and handled. */
  function handleBlockedAttempt() {
    var groups = errorGroups();
    if (!groups.length) return false;
    revealGroup(groups[0]);
    showToast();
    return true;
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  var ATC_SELECTOR = 'button[name="add"], .gpo-clone-atc-button, .product-form__submit';

  /* Globo validates on click; poll briefly afterwards so we catch it whether
     its validation is synchronous or async, without double-scrolling. */
  function watchAfterAttempt() {
    var handled = false;
    [100, 300, 650, 1100].forEach(function (delay) {
      setTimeout(function () {
        if (handled) return;
        if (handleBlockedAttempt()) handled = true;
      }, delay);
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest(ATC_SELECTOR)) return;
    watchAfterAttempt();
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.getAttribute) return;
    if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;
    watchAfterAttempt();
  }, true);

  /* Once the customer fixes the option, retire the toast immediately. */
  var debounce = null;
  var observer = new MutationObserver(function () {
    clearTimeout(debounce);
    debounce = setTimeout(function () {
      if (!errorGroups().length) hideToast();
    }, 120);
  });

  function startObserving() {
    var root = document.querySelector('.gpo-app') || document.body;
    if (!root) return;
    observer.observe(root, { subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }
  /* Globo mounts asynchronously — re-point the observer once it exists. */
  setTimeout(startObserving, 2000);
})();
